// ============================================================================
// CUSTOS — Portfolio Display Store (route-scoped)
//
// Owns /portfolio page display state: stats, rollockPositions,
// activePredictions, closedPredictions, activities. Registered via
// `providers: [PortfolioDisplayStore]` on the portfolio route so an
// instance is created on entry and destructed on leave — forcing fresh
// data on re-entry without a cache-bust path.
//
// Cross-cutting state (rewards, settleOrders$, getUnsettled*$) lives on
// the root `PortfolioStore` because settle-dialog / claim-dialog / bet-
// dialog / sidebar consume those from outside /portfolio.
//
// Carved out of PortfolioStore in docs/tasks/plan.md Phase C (commits
// 7ce325c, e73ec71, 28164fc, bbdbb58, c192f40, a77a5b2). See
// docs/state-management.md §3b for the route-scoped pattern rationale.
// ============================================================================

import { Injectable, computed, inject, signal } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { Observable, catchError, forkJoin, from, of, take, tap } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import type { Address } from 'viem';

import { API_SERVICE, type CustosApi } from '../../core/api/api.provider';
import { MarketCacheService } from '../../core/services/market-cache.service';
import { WalletService } from '../../core/services/wallet';
import { CUSTOS_ENVIRONMENT, type CustosEnvironment } from '../../core/models/environment';
import { TOKEN_LOCKER_ABI } from '../../core/contracts/abis/token-locker.abi';
import { ERC20_ABI } from '../../core/contracts/abis/erc20.abi';
import type { ApiLocker, ApiMarket, LockerRecord, StakingRewardPending } from '../../core/api/api.types';
import { PREDICT_MARKET_ABI } from '../../core/contracts/abis/predict-market.abi';
import {
  apiOrderToPredictionActive,
  apiOrderToPredictionClosed,
  apiSettlementToPredictionClosed,
  buildCycleTitle,
  parseMarketId,
  safeBigInt,
  sortByTimeDesc,
  deriveLockerAmounts,
  weiToHuman,
} from '../../core/data/data-transform';
import type { ApiCycle, ApiOrder, ApiOrderSettlement, PagedResult } from '../../core/api/api.types';
import type {
  PortfolioStats,
  RollockPosition,
  PredictionActive,
  PredictionClosed,
  PortfolioActivity,
  PortfolioReward,
} from '../../shared/models';

interface PortfolioDisplayStoreState {
  stats: PortfolioStats[];
  rollockPositions: RollockPosition[];
  activePredictions: PredictionActive[];
  closedPredictions: PredictionClosed[];
  activities: PortfolioActivity[];
  loadingCount: number;
  error: string | null;
  statsFetchedAt: number | null;
  positionsFetchedAt: number | null;
  activePredictionsFetchedAt: number | null;
  closedPredictionsFetchedAt: number | null;
  activitiesFetchedAt: number | null;
  // Pagination: hasMore flags per collection
  hasMoreActivePredictions: boolean;
  hasMoreClosedPredictions: boolean;
  hasMoreActivities: boolean;
  // Per-market offset tracking for closed predictions pagination
  closedPredictionOffsets: Record<string, number>;
}

const PAGE_SIZE = 20;
const CLOSED_PAGE_SIZE = 50;

const INITIAL_STATE: PortfolioDisplayStoreState = {
  stats: [],
  rollockPositions: [],
  activePredictions: [],
  closedPredictions: [],
  activities: [],
  loadingCount: 0,
  error: null,
  statsFetchedAt: null,
  positionsFetchedAt: null,
  activePredictionsFetchedAt: null,
  closedPredictionsFetchedAt: null,
  activitiesFetchedAt: null,
  hasMoreActivePredictions: false,
  hasMoreClosedPredictions: false,
  hasMoreActivities: false,
  closedPredictionOffsets: {},
};

// Cache duration: 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

/**
 * Route-scoped display store. Owns portfolio-page-only state (stats /
 * positions / active+closed predictions / activities). Registered via
 * `providers: [PortfolioDisplayStore]` on the /portfolio route so state
 * destructs when the user navigates away and re-fetches on re-entry.
 *
 * Cross-cutting state (rewards, settleOrders, getUnsettled*) stays on the
 * root `PortfolioStore` because those are consumed from shared dialogs and
 * the sidebar — not just /portfolio.
 *
 * See docs/state-management.md §3a-3b for the split rationale.
 */
@Injectable()
export class PortfolioDisplayStore {
  private readonly api = inject<CustosApi>(API_SERVICE);
  private readonly marketCache = inject(MarketCacheService);
  private readonly wallet = inject(WalletService);
  private readonly env = inject<CustosEnvironment>(CUSTOS_ENVIRONMENT);
  private readonly state = signalState<PortfolioDisplayStoreState>(INITIAL_STATE);

  // ==========================================================
  // Convenience getters
  // ==========================================================

  private get chainId(): number {
    return this.env.chainId;
  }

  private get walletAddress(): Address | null {
    return this.wallet.address();
  }

  // ==========================================================
  // Contract helpers (same pattern as PredictionStore)
  // ==========================================================

  private readContract<T>(args: {
    address: Address;
    abi: readonly unknown[];
    functionName: string;
    args?: readonly unknown[];
  }): Observable<T> {
    const client = this.wallet.getPublicClient();
    return from(
      client.readContract(
        args as Parameters<typeof client.readContract>[0],
      ) as Promise<T>,
    );
  }

  // writeContract helper removed — display store is read-only. Writes
  // (settleOrders, etc.) live on root PortfolioStore.

  // ==========================================================
  // State Selectors
  // ==========================================================

  readonly isLoading = computed(() => this.state().loadingCount > 0);
  readonly error = computed(() => this.state().error);
  readonly stats = computed(() => this.state().stats);
  readonly rollockPositions = computed(() => this.state().rollockPositions);
  readonly activePredictions = computed(() => this.state().activePredictions);
  readonly closedPredictions = computed(() => this.state().closedPredictions);
  readonly activities = computed(() => this.state().activities);

  readonly hasPositions = computed(
    () =>
      this.rollockPositions().length > 0 ||
      this.activePredictions().length > 0,
  );

  // Pagination hasMore selectors
  readonly hasMoreActivePredictions = computed(() => this.state().hasMoreActivePredictions);
  readonly hasMoreClosedPredictions = computed(() => this.state().hasMoreClosedPredictions);
  readonly hasMoreActivities = computed(() => this.state().hasMoreActivities);

  // Pending staking rewards total (raw numeric value, set during loadStats)
  private readonly _pendingStakingTotal = signal(0);
  readonly pendingStakingRewardsTotal = computed(() => this._pendingStakingTotal());

  // In-flight guards for paginated loads. A double-tap on "Load more" would
  // otherwise kick off two forkJoins; the second patchState reads a stale
  // `current` (captured before the first resolves) and writes duplicated
  // rows. sortByTimeDesc only reshuffles — it doesn't dedupe.
  private readonly _loadingMoreActive = signal(false);
  private readonly _loadingMoreClosed = signal(false);
  private readonly _loadingActivities = signal(false);

  // Claim button visibility (earnings only — reward-side stays on root store)
  readonly hasClaimableEarnings = computed(
    () => this.closedPredictions().some(r => !r.claimed && r.pnl !== '--' && r.pnl !== '0.00'),
  );

  // ==========================================================
  // Private helpers
  // ==========================================================

  private startLoading(): void {
    patchState(this.state, { loadingCount: this.state().loadingCount + 1 });
  }

  private stopLoading(): void {
    patchState(this.state, { loadingCount: Math.max(0, this.state().loadingCount - 1) });
  }

  private isCacheValid(fetchedAt: number | null): boolean {
    return fetchedAt !== null && Date.now() - fetchedAt < CACHE_DURATION_MS;
  }


  // ==========================================================
  // Load Methods
  // ==========================================================

  loadStats(forceRefresh = false): void {
    if (!forceRefresh && this.isCacheValid(this.state().statsFetchedAt)) return;

    this.startLoading();

    const addr = this.walletAddress;
    if (!addr) {
      this.stopLoading();
      patchState(this.state, {
        stats: [
          { icon: 'assets/icons/stat-rollocked-token.svg', label: 'PORTFOLIO.POSITIONS', value: '—' },
          { icon: 'assets/icons/stat-prediction-volume.svg', label: 'PORTFOLIO.PNL_LABEL', value: '—' },
          { icon: 'assets/icons/stat-staking-fee.svg', label: 'PORTFOLIO.ROLLOCKER_REWARDS', value: '—' },
          { icon: 'assets/icons/stat-rollocked-token.svg', label: 'PORTFOLIO.ORDER_AMOUNT', value: '—' },
        ],
        statsFetchedAt: Date.now(),
      });
      return;
    }

    forkJoin({
      lockers: this.api.getLockers({ chain_id: this.chainId }).pipe(
        catchError(() => of([] as ApiLocker[])),
      ),
      markets: this.marketCache.getMarkets().pipe(catchError(() => of([]))),
      stakingPending: this.api.getStakingRewardsPending({ user_address: addr }).pipe(
        catchError(() => of([] as StakingRewardPending[])),
      ),
      // #136: Rollocker Rewards stat = sum of all historical reward_amount_usd
      // from the locker_record table (filtered to this user).
      rewardRecords: this.api.getLockerRecords({ limit: 500, user_address: addr }).pipe(
        catchError(() => of([] as LockerRecord[])),
      ),
    }).pipe(
      switchMap(({ lockers, markets, stakingPending, rewardRecords }) => {
        // Read allLocks(user) from each locker and get token prices
        const lockRequests = lockers.length > 0
          ? lockers.map((locker) =>
              this.readContract<readonly [bigint, number, number]>({
                address: locker.locker_contract as Address,
                abi: TOKEN_LOCKER_ABI,
                functionName: 'allLocks',
                args: [addr],
              }).pipe(
                map((info) => ({
                  lockerId: locker.locker_id,
                  amount: weiToHuman(info[0], locker.base_token_decimals ?? 18),
                })),
                catchError(() => of({ lockerId: locker.locker_id, amount: 0 })),
              ),
            )
          : [of({ lockerId: '', amount: 0 })];

        // Fetch USD prices for each locker
        const priceRequests = lockers.length > 0
          ? lockers.map((locker) =>
              this.api.getLockerPrice({ chain_id: this.chainId, locker_id: locker.locker_id }).pipe(
                map((res) => ({ lockerId: locker.locker_id, price: Number(res.price) || 0 })),
                catchError(() => of({ lockerId: locker.locker_id, price: 0 })),
              ),
            )
          : [of({ lockerId: '', price: 0 })];

        return forkJoin({ locks: forkJoin(lockRequests), prices: forkJoin(priceRequests) }).pipe(
          switchMap(({ locks, prices }) => {
            // Build a price map: locker_id → USD price
            const priceMap = new Map<string, number>();
            for (const p of prices) {
              if (p.lockerId && p.price > 0) priceMap.set(p.lockerId, p.price);
            }
            const hasPrices = priceMap.size > 0;

            // Build decimals map from lockers for staking reward conversion
            const decimalsMap = new Map(lockers.map(l => [l.locker_id, l.base_token_decimals ?? 18]));

            // #136: Rollocker Rewards stat = sum of historical rewards from
            // locker_record. Each row's reward_amount is wei; divide by the
            // matching locker's decimals, multiply by that locker's USD price.
            const rewardsRawByLocker = new Map<string, number>();
            let rollockerRewardsUsd = 0;
            let rollockerRewardsRaw = 0;
            for (const r of rewardRecords) {
              const decimals = decimalsMap.get(r.locker_id) ?? 18;
              const amount = weiToHuman(safeBigInt(r.reward_amount), decimals);
              const price = priceMap.get(r.locker_id) ?? 0;
              rollockerRewardsUsd += amount * price;
              rollockerRewardsRaw += amount;
              rewardsRawByLocker.set(
                r.locker_id,
                (rewardsRawByLocker.get(r.locker_id) ?? 0) + amount,
              );
            }

            // Claim button enable state still tracks on-chain pending balances.
            const pendingStakingRaw = stakingPending.reduce((sum, r) => {
              return sum + weiToHuman(BigInt(r.total_pending_raw || '0'), decimalsMap.get(r.locker_id) ?? 18);
            }, 0);
            const pendingStakingUsd = stakingPending.reduce((sum, r) => {
              const amount = weiToHuman(BigInt(r.total_pending_raw || '0'), decimalsMap.get(r.locker_id) ?? 18);
              const price = priceMap.get(r.locker_id) ?? 0;
              return sum + amount * price;
            }, 0);
            this._pendingStakingTotal.set(hasPrices ? pendingStakingUsd : pendingStakingRaw);

            const stakingRewardsValue = hasPrices
              ? `$${rollockerRewardsUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : rollockerRewardsRaw > 0
                ? rollockerRewardsRaw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '$0.00';

            // Compute locked value in USD (or raw token amount as fallback)
            const lockedUsd = locks.reduce((sum, l) => {
              const price = priceMap.get(l.lockerId) ?? 0;
              return sum + l.amount * price;
            }, 0);
            const lockedRaw = locks.reduce((sum, l) => sum + l.amount, 0);

            if (markets.length === 0) {
              const posValue = hasPrices
                ? `$${lockedUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : `$${lockedRaw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              return of([
                { icon: 'assets/icons/stat-rollocked-token.svg', label: 'PORTFOLIO.POSITIONS', value: posValue },
                { icon: 'assets/icons/stat-prediction-volume.svg', label: 'PORTFOLIO.PNL_LABEL', value: '$0.00' },
                { icon: 'assets/icons/stat-staking-fee.svg', label: 'PORTFOLIO.ROLLOCKER_REWARDS', value: stakingRewardsValue },
                { icon: 'assets/icons/stat-rollocked-token.svg', label: 'PORTFOLIO.ORDER_AMOUNT', value: '0' },
              ]);
            }

            const orderRequests = markets.map((m) =>
              forkJoin({
                // Pre-existing bound: a single unpaged slice of up to 500
                // orders per market. A user with 500+ orders in ONE
                // market would have the overflow uncounted in Positions.
                // Out of scope for the 2026-05-22 classification fix
                // (whale-tier outlier); revisit with full pagination if
                // it ever bites.
                orders: this.api.getOrders({
                  chain_id: m.chain_id,
                  market_id: m.market_id,
                  user_address: addr,
                  limit: 500,
                }).pipe(catchError(() => of([] as ApiOrder[]))),
                cycles: this.api.getCycles({
                  chain_id: m.chain_id,
                  market_id: m.market_id,
                  limit: 500,
                }).pipe(catchError(() => of([] as ApiCycle[]))),
                settlements: this.api.getOrderSettlements({
                  chain_id: m.chain_id,
                  market_id: m.market_id,
                  user_address: addr,
                }).pipe(catchError(() => of([] as ApiOrderSettlement[]))),
              }),
            );

            return forkJoin(orderRequests).pipe(
              map((results) => {
                // Compute order counts, position values, and PNL from settlements
                let totalOrderUsd = 0;
                let totalOrderRaw = 0;
                let totalOrderCount = 0;
                let totalPnlRaw = 0;
                let totalPnlUsd = 0;
                results.forEach((result, i) => {
                  const marketId = markets[i].market_id;
                  const { base } = parseMarketId(marketId);
                  const marketPrice = priceMap.get(base) ?? 0;

                  // #71: Order Amount = total number of prediction orders (all orders)
                  totalOrderCount += result.orders.length;

                  // Positions counts ONLY open prediction positions.
                  // Summing all historical orders inflated Positions with
                  // lifetime gross volume — Wesley 2026-05-22 saw $18,800
                  // right after a withdraw because every past order was
                  // still counted.
                  //
                  // An order is REALIZED (excluded) when EITHER signal
                  // says so — the two cover each other's gaps so the
                  // result never depends on /cycles pagination ordering
                  // (Codex 2026-05-22):
                  //  - its cycle is SETTLED/RESOLVED — catches an order
                  //    whose cycle just resolved but whose per-order
                  //    settlement row has not synced yet;
                  //  - it has a settlement row — catches an OLD order
                  //    whose cycle has scrolled out of the cycle fetch
                  //    window (settlement sync long since caught up).
                  // An order that neither signal flags is treated as
                  // OPEN. A genuinely open order is always covered: its
                  // BETTING cycle is current, so it is in the fetch; and
                  // it has no settlement. So "treated as open" can only
                  // catch real open positions — never a silent undercount.
                  const realizedCycleIds = new Set(
                    result.cycles
                      .filter(
                        (c) =>
                          c.cycle_id != null &&
                          (c.state === 'SETTLED' || c.state === 'RESOLVED'),
                      )
                      .map((c) => c.cycle_id),
                  );
                  const settledOrderIds = new Set(
                    result.settlements.map((s) => s.order_id),
                  );
                  const openOrderTotal = result.orders
                    .filter(
                      (o) =>
                        !realizedCycleIds.has(o.cycle_id) &&
                        !settledOrderIds.has(o.order_id),
                    )
                    .reduce((s, o) => s + (Number(o.amount) || 0), 0);
                  totalOrderUsd += openOrderTotal * marketPrice;
                  totalOrderRaw += openOrderTotal;

                  // Compute PNL from settlements: reward - original order amount
                  const orderMap = new Map(result.orders.map((o) => [o.order_id, o]));
                  for (const settlement of result.settlements) {
                    const reward = Number(settlement.reward) || 0;
                    const matchedOrder = orderMap.get(settlement.order_id);
                    const orderAmount = matchedOrder ? (Number(matchedOrder.amount) || 0) : reward;
                    const pnl = reward - orderAmount;
                    totalPnlRaw += pnl;
                    totalPnlUsd += pnl * marketPrice;
                  }
                });

                // #71: Positions = staking + all prediction order value
                const posTotal = hasPrices ? lockedUsd + totalOrderUsd : lockedRaw + totalOrderRaw;
                const posValue = `$${posTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                // #71: Order Amount = total prediction order count
                const orderValue = String(totalOrderCount);
                const pnlTotal = hasPrices ? totalPnlUsd : totalPnlRaw;
                const pnlSign = pnlTotal >= 0 ? '+' : '';
                const pnlValue = `${pnlSign}$${pnlTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                return [
                  { icon: 'assets/icons/stat-rollocked-token.svg', label: 'PORTFOLIO.POSITIONS', value: posValue },
                  { icon: 'assets/icons/stat-prediction-volume.svg', label: 'PORTFOLIO.PNL_LABEL', value: pnlValue },
                  { icon: 'assets/icons/stat-staking-fee.svg', label: 'PORTFOLIO.ROLLOCKER_REWARDS', value: stakingRewardsValue },
                  { icon: 'assets/icons/stat-rollocked-token.svg', label: 'PORTFOLIO.ORDER_AMOUNT', value: orderValue },
                ];
              }),
            );
          }),
        );
      }),
      take(1),
      tap((stats) => {
        this.stopLoading();
        patchState(this.state, { stats, statsFetchedAt: Date.now() });
      }),
      catchError((err: Error) => {
        console.error('[PortfolioDisplayStore] Failed to load stats:', err);
        this.stopLoading();
        patchState(this.state, {
          error: err.message || 'Failed to load portfolio stats',
        });
        return of(null);
      }),
    ).subscribe();
  }

  loadPositions(forceRefresh = false): void {
    if (!forceRefresh && this.isCacheValid(this.state().positionsFetchedAt)) return;

    // Mark as in-flight BEFORE the async call starts. This prevents a
    // concurrent `loadAll(true)` from launching a second subscription that
    // produces duplicate data when both resolve and patchState.
    patchState(this.state, { positionsFetchedAt: Date.now() });
    this.startLoading();

    const addr = this.walletAddress;
    if (!addr) {
      this.stopLoading();
      patchState(this.state, { rollockPositions: [], positionsFetchedAt: Date.now() });
      return;
    }

    // Fetch lockers AND markets, then:
    // 1. Read allLocks(user) from each locker (staking amounts)
    // 2. Read unsettledOrderIds + order details from each market (predicting amounts from locker)
    forkJoin({
      lockers: this.api.getLockers({ chain_id: this.chainId }).pipe(catchError(() => of([] as ApiLocker[]))),
      markets: this.marketCache.getMarkets().pipe(catchError(() => of([] as ApiMarket[]))),
    }).pipe(
      switchMap(({ lockers, markets }) => {
        if (lockers.length === 0) return of([] as RollockPosition[]);

        // Step 1: Read staking data from each locker
        const lockRequests = lockers.map((locker) =>
          this.readContract<readonly [bigint, number, number]>({
            address: locker.locker_contract as Address,
            abi: TOKEN_LOCKER_ABI,
            functionName: 'allLocks',
            args: [addr],
          }).pipe(catchError(() => of([0n, 0, 0] as const))),
        );

        // Step 2: For each market, read unsettled order IDs then each order's details
        // balanceFrom: 0 = from locker, 1 = from wallet (per contract convention)
        const predictionRequests = markets.map((m) =>
          this.readContract<readonly bigint[]>({
            address: m.prediction_contract as Address,
            abi: PREDICT_MARKET_ABI,
            functionName: 'unsettledOrderIds',
            args: [addr],
          }).pipe(
            catchError(() => of([] as readonly bigint[])),
            switchMap((orderIds) => {
              if (orderIds.length === 0) return of({ marketId: m.market_id, lockerAmount: 0 });
              const orderReads = orderIds.map((orderId) =>
                this.readContract<readonly [string, bigint, bigint, number, bigint, bigint, boolean, number]>({
                  address: m.prediction_contract as Address,
                  abi: PREDICT_MARKET_ABI,
                  functionName: 'orders',
                  args: [orderId],
                }).pipe(
                  map((order) => {
                    // order[2] = amount, order[7] = balanceFrom (0 = locker, 1 = wallet)
                    const amount = order[2];
                    const balanceFrom = order[7];
                    return balanceFrom === 0 ? weiToHuman(amount, m.base_token_decimals ?? 18) : 0;
                  }),
                  catchError(() => of(0)),
                ),
              );
              return forkJoin(orderReads).pipe(
                map((amounts) => ({
                  marketId: m.market_id,
                  lockerAmount: amounts.reduce((sum, a) => sum + a, 0),
                })),
              );
            }),
          ),
        );

        return forkJoin({
          locks: forkJoin(lockRequests),
          predictions: markets.length > 0
            ? forkJoin(predictionRequests)
            : of([] as { marketId: string; lockerAmount: number }[]),
        }).pipe(
          map(({ locks, predictions }) => {
            // Build a map: locker_id (base token) → total predicting amount from locker
            const predictingMap = new Map<string, number>();
            for (const p of predictions) {
              const { base } = parseMarketId(p.marketId);
              predictingMap.set(base, (predictingMap.get(base) ?? 0) + p.lockerAmount);
            }

            // Dedup lockers by locker_id — Guy's D1 sync can
            // temporarily create duplicate rows for the same locker.
            const seen = new Set<string>();
            return lockers.map((locker, i) => {
              if (seen.has(locker.locker_id)) return null;
              seen.add(locker.locker_id);
              const lockInfo = locks[i];
              const decimals = locker.base_token_decimals ?? 18;
              // deriveLockerAmounts centralises the expiry semantic:
              // post-expiry, the capital migrates from Staking to
              // Withdrawable so the same money is never shown in both
              // columns (Wesley 2026-05-25 recurring report).
              const derived = deriveLockerAmounts(lockInfo, decimals);
              if (derived.rawLocked === 0) return null;
              const predictingAmount = predictingMap.get(locker.locker_id) ?? 0;
              // Total = all capital the user has in this locker, in any
              // state. Pre-expiry: staking + predicting. Post-expiry:
              // withdrawable + predicting. Either way the sum is stable.
              const totalAmount =
                derived.spendableStaking + predictingAmount + derived.withdrawable;
              return {
                token: locker.locker_id,
                tokenIcon: '',
                totalAmount,
                stakingAmount: derived.spendableStaking,
                predictingAmount,
                remainingAmount: derived.withdrawable,
                endTime: new Date(derived.endTime * 1000).toLocaleString('sv-SE', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                }),
              } satisfies RollockPosition;
            }).filter((r): r is RollockPosition => r !== null);
          }),
        );
      }),
      take(1),
      tap((rollockPositions) => {
        this.stopLoading();
        patchState(this.state, { rollockPositions, positionsFetchedAt: Date.now() });
      }),
      catchError((err: Error) => {
        console.error('[PortfolioDisplayStore] Failed to load positions:', err);
        this.stopLoading();
        patchState(this.state, {
          error: err.message || 'Failed to load positions',
        });
        return of(null);
      }),
    ).subscribe();
  }

  loadActivePredictions(forceRefresh = false): void {
    if (!forceRefresh && this.isCacheValid(this.state().activePredictionsFetchedAt)) return;

    this.startLoading();

    const addr = this.walletAddress;
    if (!addr) {
      this.stopLoading();
      patchState(this.state, { activePredictions: [], activePredictionsFetchedAt: Date.now(), hasMoreActivePredictions: false });
      return;
    }

    const nowMs = Date.now();

    this.marketCache.getMarkets().pipe(
      switchMap((markets) => {
        if (markets.length === 0) return of({ items: [] as PredictionActive[], hasMore: false });
        // For each market: fetch orders AND cycles, only include orders whose cycle end_time is in the future
        const requests = markets.map((m) =>
          forkJoin({
            orders: this.api.getOrdersPaged({
              chain_id: m.chain_id,
              market_id: m.market_id,
              user_address: addr,
              limit: PAGE_SIZE,
              offset: 0,
            }).pipe(catchError(() => of({ items: [], total: 0, hasMore: false } as PagedResult<ApiOrder>))),
            cycles: this.api.getCycles({
              chain_id: m.chain_id,
              market_id: m.market_id,
              limit: 500,
            }).pipe(catchError(() => of([] as ApiCycle[]))),
          }).pipe(
            map(({ orders, cycles }) => {
              // Build cycle_id → end_time map
              const cycleEndMap = new Map<number, number>();
              const cycleMap = new Map<number, ApiCycle>();
              for (const c of cycles) {
                if (c.cycle_id != null) {
                  cycleEndMap.set(c.cycle_id, new Date(c.end_time).getTime());
                  cycleMap.set(c.cycle_id, c);
                }
              }
              // Only include orders whose cycle end_time has NOT passed
              const activeOrders = orders.items.filter((o) => {
                const endMs = cycleEndMap.get(o.cycle_id);
                return endMs != null && endMs > nowMs;
              });
              return {
                items: activeOrders.map((o) => {
                  const row = apiOrderToPredictionActive(o, m.market_id);
                  const cycle = cycleMap.get(o.cycle_id);
                  if (cycle) {
                    row.market = buildCycleTitle(m.market_id, cycle);
                  }
                  return row;
                }),
                hasMore: orders.hasMore,
              };
            }),
          ),
        );
        return forkJoin(requests).pipe(
          map((results) => ({
            items: results.flatMap((r) => r.items),
            hasMore: results.some((r) => r.hasMore),
          })),
        );
      }),
      take(1),
      tap(({ items, hasMore }) => {
        this.stopLoading();
        patchState(this.state, {
          activePredictions: sortByTimeDesc(items),
          activePredictionsFetchedAt: Date.now(),
          hasMoreActivePredictions: hasMore,
        });
      }),
      catchError((err: Error) => {
        console.error('[PortfolioDisplayStore] Failed to load active predictions:', err);
        this.stopLoading();
        patchState(this.state, {
          error: err.message || 'Failed to load active predictions',
        });
        return of(null);
      }),
    ).subscribe();
  }

  loadMoreActivePredictions(): void {
    if (this._loadingMoreActive()) return;
    if (!this.state().hasMoreActivePredictions) return;

    this._loadingMoreActive.set(true);
    this.startLoading();
    const addr = this.walletAddress;
    if (!addr) { this.stopLoading(); this._loadingMoreActive.set(false); return; }
    const current = this.state().activePredictions;

    this.marketCache.getMarkets().pipe(
      switchMap((markets) => {
        if (markets.length === 0) return of({ items: [] as PredictionActive[], hasMore: false });
        const requests = markets.map((m) =>
          this.api.getOrdersPaged({
            chain_id: m.chain_id,
            market_id: m.market_id,
            user_address: addr,
            limit: PAGE_SIZE,
            offset: current.length,
          }).pipe(
            map((page) => ({
              items: page.items.map((o) => apiOrderToPredictionActive(o, m.market_id)),
              hasMore: page.hasMore,
            })),
            catchError(() => of({ items: [] as PredictionActive[], hasMore: false })),
          ),
        );
        return forkJoin(requests).pipe(
          map((results) => ({
            items: results.flatMap((r) => r.items),
            hasMore: results.some((r) => r.hasMore),
          })),
        );
      }),
      take(1),
      tap(({ items, hasMore }) => {
        this.stopLoading();
        // Re-read current from state — the guard above narrows races but the
        // intended merge source is always the latest persisted list.
        patchState(this.state, {
          activePredictions: sortByTimeDesc([...this.state().activePredictions, ...items]),
          hasMoreActivePredictions: hasMore,
        });
        this._loadingMoreActive.set(false);
      }),
      catchError((err: Error) => {
        console.error('[PortfolioDisplayStore] Failed to load more active predictions:', err);
        this.stopLoading();
        this._loadingMoreActive.set(false);
        return of(null);
      }),
    ).subscribe();
  }

  loadClosedPredictions(forceRefresh = false): void {
    if (!forceRefresh && this.isCacheValid(this.state().closedPredictionsFetchedAt)) return;

    this.startLoading();

    const addr = this.walletAddress;
    if (!addr) {
      this.stopLoading();
      patchState(this.state, { closedPredictions: [], closedPredictionsFetchedAt: Date.now(), hasMoreClosedPredictions: false });
      return;
    }

    const nowMs = Date.now();

    this.marketCache.getMarkets().pipe(
      switchMap((markets) => {
        if (markets.length === 0) return of({ items: [] as PredictionClosed[], hasMore: false, offsets: {} as Record<string, number> });
        // For each market: fetch orders, cycles, and settlements.
        // Show all orders whose cycle end_time has passed.
        // Enrich with settlement payout when available; otherwise show "Pending".
        const requests = markets.map((m) =>
          forkJoin({
            orders: this.api.getOrdersPaged({
              chain_id: m.chain_id,
              market_id: m.market_id,
              user_address: addr,
              limit: CLOSED_PAGE_SIZE,
              offset: 0,
            }).pipe(catchError(() => of({ items: [], total: 0, hasMore: false } as PagedResult<ApiOrder>))),
            cycles: this.api.getCycles({
              chain_id: m.chain_id,
              market_id: m.market_id,
              limit: 500,
            }).pipe(catchError(() => of([] as ApiCycle[]))),
            settlements: this.api.getOrderSettlements({
              chain_id: m.chain_id,
              market_id: m.market_id,
              user_address: addr,
            }).pipe(catchError(() => of([] as ApiOrderSettlement[]))),
          }).pipe(
            map(({ orders, cycles, settlements }) => {
              // Build cycle_id → end_time map and cycle_id → cycle map
              const cycleEndMap = new Map<number, number>();
              const cycleMap = new Map<number, ApiCycle>();
              for (const c of cycles) {
                if (c.cycle_id != null) {
                  cycleEndMap.set(c.cycle_id, new Date(c.end_time).getTime());
                  cycleMap.set(c.cycle_id, c);
                }
              }
              // Build settlement lookup by order_id
              const settlementMap = new Map(settlements.map((s) => [s.order_id, s]));
              // Only include orders whose cycle end_time has passed
              const closedOrders = orders.items.filter((o) => {
                const endMs = cycleEndMap.get(o.cycle_id);
                return endMs != null && endMs <= nowMs;
              });
              const items: PredictionClosed[] = closedOrders.map((o) => {
                const settlement = settlementMap.get(o.order_id);
                const cycle = cycleMap.get(o.cycle_id);
                const row = settlement
                  ? apiSettlementToPredictionClosed(settlement, m.market_id, o)
                  : apiOrderToPredictionClosed(o, m.market_id, cycle);
                // Replace market_id with full cycle title when cycle data is available
                if (cycle) {
                  row.market = buildCycleTitle(m.market_id, cycle);
                }
                return row;
              });
              return { marketId: m.market_id, items, hasMore: orders.hasMore, fetchedCount: orders.items.length };
            }),
          ),
        );
        return forkJoin(requests).pipe(
          map((results) => ({
            items: results.flatMap((r) => r.items),
            hasMore: results.some((r) => r.hasMore),
            offsets: Object.fromEntries(results.map((r) => [r.marketId, r.fetchedCount])),
          })),
        );
      }),
      take(1),
      tap(({ items, hasMore, offsets }) => {
        this.stopLoading();
        // Wesley: newest settled bet on top; raw per-market fetch groups
        // by market and interleaves old/new, which confuses users.
        patchState(this.state, {
          closedPredictions: sortByTimeDesc(items),
          closedPredictionsFetchedAt: Date.now(),
          hasMoreClosedPredictions: hasMore,
          closedPredictionOffsets: offsets,
        });
      }),
      catchError((err: Error) => {
        console.error('[PortfolioDisplayStore] Failed to load closed predictions:', err);
        this.stopLoading();
        patchState(this.state, {
          error: err.message || 'Failed to load closed predictions',
        });
        return of(null);
      }),
    ).subscribe();
  }

  loadMoreClosedPredictions(): void {
    if (this._loadingMoreClosed()) return;
    if (!this.state().hasMoreClosedPredictions) return;

    this._loadingMoreClosed.set(true);
    this.startLoading();
    const addr = this.walletAddress;
    if (!addr) { this.stopLoading(); this._loadingMoreClosed.set(false); return; }

    const nowMs = Date.now();
    const currentOffsets = this.state().closedPredictionOffsets;

    this.marketCache.getMarkets().pipe(
      switchMap((markets) => {
        if (markets.length === 0) return of({ items: [] as PredictionClosed[], hasMore: false, offsets: {} as Record<string, number> });
        const requests = markets.map((m) =>
          forkJoin({
            orders: this.api.getOrdersPaged({
              chain_id: m.chain_id,
              market_id: m.market_id,
              user_address: addr,
              limit: CLOSED_PAGE_SIZE,
              offset: currentOffsets[m.market_id] ?? 0,
            }).pipe(catchError(() => of({ items: [], total: 0, hasMore: false } as PagedResult<ApiOrder>))),
            cycles: this.api.getCycles({
              chain_id: m.chain_id,
              market_id: m.market_id,
              limit: 500,
            }).pipe(catchError(() => of([] as ApiCycle[]))),
            settlements: this.api.getOrderSettlements({
              chain_id: m.chain_id,
              market_id: m.market_id,
              user_address: addr,
            }).pipe(catchError(() => of([] as ApiOrderSettlement[]))),
          }).pipe(
            map(({ orders, cycles, settlements }) => {
              const cycleEndMap = new Map<number, number>();
              const cycleMap = new Map<number, ApiCycle>();
              for (const c of cycles) {
                if (c.cycle_id != null) {
                  cycleEndMap.set(c.cycle_id, new Date(c.end_time).getTime());
                  cycleMap.set(c.cycle_id, c);
                }
              }
              const settlementMap = new Map(settlements.map((s) => [s.order_id, s]));
              const closedOrders = orders.items.filter((o) => {
                const endMs = cycleEndMap.get(o.cycle_id);
                return endMs != null && endMs <= nowMs;
              });
              const items: PredictionClosed[] = closedOrders.map((o) => {
                const settlement = settlementMap.get(o.order_id);
                const cycle = cycleMap.get(o.cycle_id);
                const row = settlement
                  ? apiSettlementToPredictionClosed(settlement, m.market_id, o)
                  : apiOrderToPredictionClosed(o, m.market_id, cycle);
                if (cycle) {
                  row.market = buildCycleTitle(m.market_id, cycle);
                }
                return row;
              });
              return { marketId: m.market_id, items, hasMore: orders.hasMore, fetchedCount: orders.items.length };
            }),
            catchError(() => of({ marketId: m.market_id, items: [] as PredictionClosed[], hasMore: false, fetchedCount: 0 })),
          ),
        );
        return forkJoin(requests).pipe(
          map((results) => ({
            items: results.flatMap((r) => r.items),
            hasMore: results.some((r) => r.hasMore),
            offsets: Object.fromEntries(
              results.map((r) => [r.marketId, (currentOffsets[r.marketId] ?? 0) + r.fetchedCount]),
            ),
          })),
        );
      }),
      take(1),
      tap(({ items, hasMore, offsets }) => {
        this.stopLoading();
        // Re-read current from state so we merge against the latest persisted
        // list, not a snapshot captured before this request resolved.
        patchState(this.state, {
          closedPredictions: sortByTimeDesc([...this.state().closedPredictions, ...items]),
          hasMoreClosedPredictions: hasMore,
          closedPredictionOffsets: offsets,
        });
        this._loadingMoreClosed.set(false);
      }),
      catchError((err: Error) => {
        console.error('[PortfolioDisplayStore] Failed to load more closed predictions:', err);
        this.stopLoading();
        this._loadingMoreClosed.set(false);
        return of(null);
      }),
    ).subscribe();
  }

  loadActivities(forceRefresh = false): void {
    if (!forceRefresh && this.isCacheValid(this.state().activitiesFetchedAt)) return;
    // In-flight guard: a page-level "Load More" button calls loadActivities(true)
    // on every click. Without this, a rapid double-tap fires two concurrent
    // forkJoins against the activity APIs (soft DoS) and races their results
    // through patchState. The fetchedAt bump alone doesn't protect force=true.
    if (this._loadingActivities()) return;

    this._loadingActivities.set(true);
    patchState(this.state, { activitiesFetchedAt: Date.now() });
    this.startLoading();

    const addr = this.walletAddress;
    if (!addr) {
      this.stopLoading();
      this._loadingActivities.set(false);
      patchState(this.state, { activities: [], activitiesFetchedAt: Date.now(), hasMoreActivities: false });
      return;
    }

    // #142: Pull BOTH rollock/extend events (locker_orders) AND unlock events
    // (locker_unlock_orders) and merge them into one Activity feed. Unlock
    // rows render as operation "Unlock" so the user sees the full lifecycle.
    forkJoin({
      rollocks: this.api.getLockerOrdersPaged({
        chain_id: this.chainId,
        user_address: addr,
        limit: PAGE_SIZE,
        offset: 0,
      }).pipe(catchError(() => of({ items: [], total: 0, hasMore: false } as PagedResult<never>))),
      unlocks: this.api.getLockerUnlockOrders({
        chain_id: this.chainId,
        user_address: addr,
        limit: PAGE_SIZE,
      }).pipe(catchError(() => of([]))),
      lockers: this.api.getLockers({ chain_id: this.chainId }).pipe(
        catchError(() => of([])),
      ),
    }).pipe(
      map(({ rollocks, unlocks, lockers }) => {
        // Decimals lookup per locker so weiToHuman is correct for non-18-dec tokens
        const decimalsByLocker = new Map(
          lockers.map((l) => [l.locker_id, l.base_token_decimals ?? 18]),
        );
        // Helper: parse a candidate timestamp into a ms epoch, or null if
        // it isn't a date. Used to merge + sort rollock/unlock rows.
        // A bare integer (block number, log index) is rejected up front:
        // `new Date(86)` is a VALID date 86 ms after the epoch, which is
        // exactly how a log index leaked through as "1969-12-31" before
        // (Wesley 2026-05-22). Only genuine date strings are accepted.
        const toEpoch = (s: unknown): number | null => {
          if (typeof s !== 'string' || s.trim() === '') return null;
          if (/^\d+$/.test(s.trim())) return null;
          const n = new Date(s).getTime();
          return Number.isNaN(n) ? null : n;
        };
        const formatTime = (ms: number): string =>
          new Date(ms).toLocaleString('sv-SE', {
            dateStyle: 'short',
            timeStyle: 'short',
          });
        type DatedActivity = PortfolioActivity & { _epoch: number };

        const rollockActivities: DatedActivity[] = rollocks.items
          .map((o): DatedActivity | null => {
            const epoch = toEpoch(o.created_at);
            if (epoch === null) return null;
            return {
              token: o.locker_id,
              amount: Number(o.amount) || 0,
              time: formatTime(epoch),
              // i18n key — template pipes through translate.
              operation: 'PORTFOLIO.OPERATION_ROLLOCK_EXTEND',
              _epoch: epoch,
            };
          })
          .filter((a): a is DatedActivity => a !== null);

        const unlockActivities: DatedActivity[] = unlocks
          .map((u): DatedActivity | null => {
            // ApiService.getLockerUnlockOrders already normalises unlock_amount
            // from wei to human decimal. Parse it directly as a number here —
            // don't re-divide via weiToHuman or every withdraw row shows 0.
            //
            // Timestamp source is `unlocked_at` (a proper ISO string).
            // Verified live 2026-05-22: `unlocked_at` carries the ISO
            // timestamp, `log_index` carries the real blockchain log
            // index. We try `unlocked_at` first; `log_index` stays as a
            // last-ditch fallback ONLY because `toEpoch` now rejects bare
            // integers, so a stray log index can no longer masquerade as
            // a date (the old 1969 bug). If a future/legacy record ever
            // puts a real date string in `log_index`, this still renders
            // it; if neither field is a date, the row is dropped — there
            // is genuinely no timestamp to show.
            const epoch = toEpoch(u.unlocked_at) ?? toEpoch(u.log_index);
            if (epoch === null) return null;
            return {
              token: u.locker_id,
              amount: Number(u.unlock_amount) || 0,
              time: formatTime(epoch),
              // i18n key — renders as "Withdraw" in English per customer #142.
              operation: 'PORTFOLIO.OPERATION_WITHDRAW',
              _epoch: epoch,
            };
          })
          .filter((a): a is DatedActivity => a !== null);

        // Sort by numeric epoch (descending), then strip the helper field.
        const merged: PortfolioActivity[] = [...rollockActivities, ...unlockActivities]
          .sort((a, b) => b._epoch - a._epoch)
          .map(({ _epoch: _, ...rest }) => rest);
        return { activities: merged, hasMore: rollocks.hasMore };
      }),
      take(1),
      tap(({ activities, hasMore }) => {
        this.stopLoading();
        this._loadingActivities.set(false);
        patchState(this.state, { activities, activitiesFetchedAt: Date.now(), hasMoreActivities: hasMore });
      }),
      catchError((err: Error) => {
        console.error('[PortfolioDisplayStore] Failed to load activities:', err);
        this.stopLoading();
        this._loadingActivities.set(false);
        patchState(this.state, {
          error: err.message || 'Failed to load activities',
        });
        return of(null);
      }),
    ).subscribe();
  }

  /** Refresh all display collections. Rewards live on root PortfolioStore;
   *  /portfolio page triggers root.loadRewards separately. */
  loadAll(forceRefresh = false): void {
    this.loadStats(forceRefresh);
    this.loadActivePredictions(forceRefresh);
    this.loadClosedPredictions(forceRefresh);
    this.loadPositions(forceRefresh);
    this.loadActivities(forceRefresh);
  }

  updateClosedPredictions(rows: PredictionClosed[]): void {
    patchState(this.state, { closedPredictions: sortByTimeDesc(rows) });
  }


  /**
   * Load wallet balance for a locker's base ERC-20 token.
   * Finds the locker contract, reads its `token()`, then reads `balanceOf(wallet)`.
   * Returns human-readable balance.
   */
  loadTokenBalance$(lockerId: string): Observable<number> {
    const addr = this.walletAddress;
    if (!addr) return of(0);

    return this.api.getLockers({ chain_id: this.chainId }).pipe(
      switchMap((lockers) => {
        const locker = lockers.find((l) => l.locker_id === lockerId);
        if (!locker) return of(0);
        const lockerAddr = locker.locker_contract as Address;
        const decimals = locker.base_token_decimals ?? 18;

        return from(
          this.wallet.getPublicClient().readContract({
            address: lockerAddr,
            abi: TOKEN_LOCKER_ABI,
            functionName: 'token',
          }),
        ).pipe(
          switchMap((tokenAddr) =>
            from(
              this.wallet.getPublicClient().readContract({
                address: tokenAddr as Address,
                abi: ERC20_ABI,
                functionName: 'balanceOf',
                args: [addr],
              }),
            ).pipe(
              map((bal) => weiToHuman(bal as bigint, decimals)),
            ),
          ),
        );
      }),
      catchError(() => of(0)),
      take(1),
    );
  }


  // ==========================================================
  // Utility
  // ==========================================================

  clear(): void {
    patchState(this.state, INITIAL_STATE);
  }
}
